import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Compass, Copy, Check } from 'lucide-react';
import { Message, Location } from '../types/chat';
import { processStreamingMessage } from '../services/chat/messageProcessor';
import { DefaultWeatherWidget } from './weather/DefaultWeatherWidget';
import { NextBestActions } from './NextBestActions';
import { HelperPrompts } from './HelperPrompts';
import { DataSyncService } from '../services/dataPersistence/dataSync';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MessageComponentFactory } from './MessageComponentFactory';
import { ExtractionLoader } from './ExtractionLoader';
import { ExtractionStatus } from '../types/chat';
import { LocationRecommendation } from './LocationRecommendation';
import { getLocationTypeAndIcon } from '../utils/locationTypeUtils';
import StackedLocationCarousel from './StackedLocationCarousel';




import { 
  MapPin, Utensils, Coffee, ShoppingBag, Building2, Trees, 
  Camera, Landmark, Hotel, Wine, Umbrella, Mountain,
  Church, Castle, Theater, BookOpen, Palette
} from 'lucide-react';

import { ImageStorage } from '../services/imageStorage';
import { conversationService, dataSyncService } from '../services/dataPersistence';


const renderContentWithLocationLinks_OLD = (
  content: string, 
  locations: Location[], 
  onLocationClick: (location: Location) => void,
  selectedLocation: Location | null
) => {
  if (!locations.length) return content;

  // Create a regex pattern from location names
  const locationNames = locations.map(loc => {
    const displayName = loc.name.split(',')[0].trim();
    return displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  });
  
  const pattern = new RegExp(`(${locationNames.join('|')})`, 'gi');
  
  // Split content and create elements
  const parts = content.split(pattern);
  
  return parts.map((part, index) => {
    // Check if this part matches a location name
    const matchedLocation = locations.find(loc => {
      const displayName = loc.name.split(',')[0].trim();
      return displayName.toLowerCase() === part.toLowerCase();
    });
    
    if (matchedLocation) {
      const isActive = selectedLocation?.id === matchedLocation.id;
      
      return (
        <span
          key={`${matchedLocation.id}-${index}`}
          className="inline-flex items-center cursor-pointer transition-all duration-200 location-link"
          style={{
            backgroundColor: isActive ? '#60a5fa' : '#e0e7ff',
            color: isActive ? 'white' : '#4338ca',
            padding: '2px 8px 2px 6px',
            borderRadius: '12px',
            fontSize: 'inherit',
            fontWeight: isActive ? '500' : 'inherit',
            marginLeft: '2px',
            marginRight: '2px',
            textDecoration: 'none',
            verticalAlign: 'baseline',
            alignItems: 'center',
            gap: '2px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onLocationClick(matchedLocation);
          }}
          onMouseEnter={(e) => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = '#c7d2fe';
            }
            window.dispatchEvent(new CustomEvent('tripper-location-hover', {
              detail: { locationId: matchedLocation.id }
            }));
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = '#e0e7ff';
            }
            window.dispatchEvent(new CustomEvent('tripper-location-hover-end'));
          }}
        >
          {getLocationIconForMessage(matchedLocation)}
          <span>{part.replace(/\*\*/g, '')}</span>
        </span>
      );
    }
    
    // For non-location parts, just remove all ** markers
    return <span key={index}>{part.replace(/\*\*/g, '')}</span>;
  });
};

const renderContentWithLocationLinks = (
  content: string, 
  locations: Location[], 
  onLocationClick: (location: Location) => void,
  selectedLocation: Location | null
) => {
  if (!locations.length) return content;

  // Create a regex pattern from location names
  const locationNames = locations.map(loc => {
    const displayName = loc.name.split(',')[0].trim();
    return displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  });
  
  const pattern = new RegExp(`(${locationNames.join('|')})`, 'gi');
  
  // Split content and create elements
  const parts = content.split(pattern);
  
  return parts.map((part, index) => {
    // Check if this part matches a location name
    const matchedLocation = locations.find(loc => {
      const displayName = loc.name.split(',')[0].trim();
      return displayName.toLowerCase() === part.toLowerCase();
    });
    
    if (matchedLocation) {
      const isActive = selectedLocation?.id === matchedLocation.id;
      
      return (
        <span
          key={`${matchedLocation.id}-${index}`}
          className="inline-flex items-center cursor-pointer transition-all duration-200 location-link"
          style={{
            backgroundColor:'transparent', //  isActive ? '#e0e7ff' :  subtle gray when active, transparent when not
            color: isActive ? '#4338ca' : '#000000', // black when active, purple when not
            //padding: '2px 8px 2px 6px',
            borderRadius: '12px',
            fontSize: 'inherit',
            fontWeight: isActive ? '4100' : 'inherit', // slightly bolder when active
            marginLeft: '2px',
            marginRight: '0px',
            textDecoration: 'none',
            verticalAlign: 'baseline',
            alignItems: 'center',
            gap: '2px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onLocationClick(matchedLocation);
          }}
          onMouseEnter={(e) => {
            if (!isActive) {
              //e.currentTarget.style.backgroundColor = '#f9fafb'; // very light gray on hover
            }
            window.dispatchEvent(new CustomEvent('tripper-location-hover', {
              detail: { locationId: matchedLocation.id }
            }));
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = 'transparent'; // back to transparent
            }
            window.dispatchEvent(new CustomEvent('tripper-location-hover-end'));
          }}
        >
          {getLocationIconForMessage(matchedLocation)}
          <span style={{ fontWeight: 600 }}>{part.replace(/\*\*/g, '')}</span>
        </span>
      );
    }
    
    // For non-location parts, just remove all ** markers
    return <span key={index}>{part.replace(/\*\*/g, '')}</span>;
  });
};

// Replace getLocationIconForMessage with:
const getLocationIconForMessage = (location: Location) => {
  const { icon } = getLocationTypeAndIcon(location, 14);
  return icon;
};

interface ChatMessageProps {
  message: Message;
  isStreaming?: boolean;
  onLocationsUpdate: (locations: Location[]) => void;
  onLocationSelect: (location: Location | null) => void; // Add | null here
  selectedLocation: Location | null;
  onActionClick: (action: string) => void;
  isRebuildMode?: boolean;  // Flag for database rebuild mode
  isLatestMessage?: boolean; // Flag to identify the latest message
  isContinuation?: boolean; // NEW: Indicates this is part of continuous conversation
  isLastInSequence?: boolean; // NEW: Indicates this is the last message in a sequence
}

export function ChatMessage({ 
  message,
  onLocationsUpdate,
  isStreaming = false,
  onLocationSelect,
  selectedLocation,
  onActionClick,
  isRebuildMode = false,
  isLatestMessage = false,
  isContinuation = false, // NEW
  isLastInSequence = true  // NEW - default true for backward compatibility
}: ChatMessageProps) {
  const [displayContent, setDisplayContent] = useState('');
  const [locations, setLocations] = useState<Location[]>([]);
  const [showLocations, setShowLocations] = useState(false);
  const [weatherLocation, setWeatherLocation] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const isBot = message.sender === 'bot';
  const showAvatar = !isContinuation; // Only show avatar for first message
  const showTimestampAndCopy = isLastInSequence && !isStreaming && message.type !== 'help';

  const processedRef = useRef(false);

  // Get components from factory
  const components = MessageComponentFactory.extractComponents(message);
  //console.log('[WEATHER-DEBUG] Components extracted:', components);

  const { extractionStatus, extractionProgress, extractionStartTime } = message;
  
  // Normal message processing for live conversations
  useEffect(() => {
    if (!message.content) {
      // --- console.log('[ChatMessage] No content, isStreaming:', isStreaming);
      setDisplayContent(isStreaming ? ' ' : '');
      return;
    }

    if (message.type === 'image') {
      setDisplayContent('');
      return;
    }

    try {
      const { textContent, jsonContent, weatherLocation } = processStreamingMessage(message.content)

      setDisplayContent(textContent || (isStreaming ? ' ' : ''));
      
      if (weatherLocation) {
        //console.log('[WEATHER-DEBUG] Setting weatherLocation:', weatherLocation);
        setWeatherLocation(weatherLocation);
        
        // Add to message for factory to find
        if (!message._extractedData) {
          message._extractedData = {};
        }
        message._extractedData.weather = { location: weatherLocation };
        //console.log('[WEATHER-DEBUG] Updated message._extractedData:', message._extractedData);
        return;
      }

      const locationsToProcess = message._extractedData?.locations || message._extractedData?.restaurant || [];

      // Check for extracted data instead of parsing JSON
      if (locationsToProcess && !isStreaming && !processedRef.current) {
        //console.log('[ChatMessage] Processing locations:', locationsToProcess);
        const processedLocations = locationsToProcess
        .filter(loc => loc && loc.position && loc.position.lat && loc.position.lng)
        .map((loc: any, index: number) => ({
          id: loc.id || `loc-${Date.now()}-${index}`,
          name: loc.name,
          position: {
            lat: Number(loc.position.lat),
            lng: Number(loc.position.lng)
          },
          city: loc.city,
          country: loc.country || '',
          rating: loc.rating || 4.5,
          reviews: loc.reviews || 1000,
          description: loc.description || '',
          imageUrl: loc.imageUrl || '',
          type: loc.type || 'place',
          cuisine: loc.cuisine || null,
          priceLevel: loc.priceLevel ?? null,
          formattedAddress: loc.formattedAddress || null,
          businessStatus: loc.businessStatus || null,
          placeId: loc.placeId || null,
          types: loc.types || null
        }));
        
  
        if (processedLocations.length > 0) {
          //console.log('[ChatMessage] rendering location cards');
          processedRef.current = true;
          onLocationsUpdate(processedLocations);
          setLocations(processedLocations);
          setShowLocations(true);
          onLocationSelect(processedLocations[0]);
        }
      }
    } catch (error) {
      console.error('[ChatMessage] Error processing message:', error);
      setDisplayContent(message.content);
    }
  }, [message._extractedData, message, isStreaming, onLocationSelect, onLocationsUpdate]);


  // Rebuilt conversation locations handling
  useEffect(() => {
    // Check for restored locations immediately
    if (!processedRef.current && !isStreaming && window.messageLocationMap) {
      const associatedLocations = window.messageLocationMap[message.id];
      if (associatedLocations && associatedLocations.length > 0) {
        
        const hasValidPosition = associatedLocations[0] && 
                               associatedLocations[0].position && 
                               typeof associatedLocations[0].position === 'object' &&
                               'lat' in associatedLocations[0].position && 
                               'lng' in associatedLocations[0].position;
        
        processedRef.current = true;
        
        // Always update local state to show location cards
        setLocations(associatedLocations);
        setShowLocations(true);
        
        // CRITICAL: Only update parent's location state for the latest message during rebuild
        if (isRebuildMode) {
          if (isLatestMessage) {
            
            // More robust parsing for coordinates
            const processedLocations = associatedLocations.map((loc, idx) => {
              
              // Direct check for database format like in your example
              if (loc.position && typeof loc.position === 'object' && 'lat' in loc.position && 'lng' in loc.position) {
                const position = {
                  lat: Number(loc.position.lat),
                  lng: Number(loc.position.lng)
                };
                
                return {
                  ...loc,
                  position: position
                };
              }
              
              // Fallback for other potential formats
              const fallbackLat = 0;
              const fallbackLng = 0;
              
              console.error(`[ChatMessage] Invalid position format for location:`, loc);
              
              return {
                ...loc,
                position: {
                  lat: fallbackLat,
                  lng: fallbackLng
                }
              };
            });
            
            // Update with properly formatted locations
            onLocationsUpdate(processedLocations);
            onLocationSelect(processedLocations[0]);
          }
        } else {
          // Normal mode - update map for all messages with proper number formatting
          const processedLocations = associatedLocations.map(loc => ({
            ...loc,
            position: {
              lat: Number(loc.position.lat),
              lng: Number(loc.position.lng)
            }
          }));
          onLocationsUpdate(processedLocations);
          onLocationSelect(processedLocations[0]);
        }
      }
    }
  }, [message.id, isStreaming, isRebuildMode, isLatestMessage, onLocationsUpdate, onLocationSelect]);

  // For location cards, add this to the ChatMessage component:
  useEffect(() => {
    // After locations are processed
    if (locations.length > 0 && showLocations) {
      // Tell App to scroll after location cards render
      setTimeout(() => {
        // Dispatch a custom event that App can listen for
        window.dispatchEvent(new CustomEvent('tripper-content-added'));
      }, 100);
    }
  }, [locations, showLocations]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  // Add this before the return statement
  const weatherComponents = components.filter(c => c.type === 'weather' && c.position === 'after-message');
  //console.log('[WEATHER-DEBUG] Weather components to render:', weatherComponents);

  return (
    <div style={{ marginBottom: !isBot ? 'var(--spacing-lg)' : isContinuation ? 'var(--spacing-sm)' : 'var(--spacing-lg)' }}>
      <div className={`flex ${isBot ? 'flex-row' : 'flex-row-reverse'}`} 
          style={{ gap: 'var(--spacing-md)' }}>
        
        <div className={`relative ${!isBot ? 'ml-auto' : ''}`} 
            style={{ width: isBot ? '100%' : 'auto' }}> {/* ADD THIS */}
            {/* Now 100% will work! */}          
          {message.type === 'image' && message.imageUrl && (
            <div className="rounded-lg max-w-[200px]">
              <img 
                src={message.imageUrl} 
                alt="Uploaded location" 
                className="w-full h-auto object-cover"
              />
            </div>
          )}
          {displayContent && (
              <div className={`rounded-lg p-3 ${isBot ? 'bg-white' : 'bg-blue-50'} relative group ${!isBot ? 'ml-auto' : ''}`}
                style={{
                  maxWidth: isBot ? 'var(--chat-bubble-max-width, 100%)' : 'var(--user-bubble-max-width, 90%)',
                  width: 'fit-content', // Use max-content instead of fit-content
                  minWidth:  'auto',//isBot ? 'var(--chat-bubble-max-width, 10%)' : 'var(--user-bubble-min-width, 80%)',
                  //whiteSpace: 'normal' // Ensure this isn't set to 'pre-wrap' or similar
                  paddingRight: '2rem', // or '1rem'
                  //paddingLeft: '1rem', // or '1rem'
                  whiteSpace: message.content.length < 20 ? 'nowrap' : 'normal'
                }}
              >
              <div className="max-w-none chat-message" style={{ 
                fontSize: 'var(--chat-font-size, 14px)',
                fontWeight: 'var(--chat-font-weight, 400)',
                lineHeight: 'var(--chat-line-height, 1) !important',
                color: 'var(--chat-text-color, #1f2937)',
                display: 'inline-block'  
              }}>
                <div className="chat-message-content" style={{ 
                  lineHeight: 'var(--chat-line-height, 1.5)',
                  whiteSpace: message.content.length < 20 ? 'nowrap' : 'pre-wrap',
                  //whiteSpace: 'pre-wrap',
                  display: 'inline-block', // Changed from 'block'
                  position: 'relative',
                  fontSize: 'var(--chat-font-size, 14px)',
                  fontWeight: 'var(--chat-font-weight, 400)',
                  color: 'var(--chat-text-color, #1f2937)',
                  marginBottom: 'var(--chat-paragraph-spacing, 0.75rem)'
                }}>
                <span>
                {locations.length > 0 && !isStreaming ? (
                  renderContentWithLocationLinks(
                    displayContent.replace(/^\n+/, ''),
                    locations,
                    onLocationSelect,
                    selectedLocation
                  )
                ) : (
                  // Parse markdown in React instead of using dangerouslySetInnerHTML
                  <span>
                    {displayContent.replace(/^\n+/, '').split(/(\*\*[^*]+\*\*|\n)/g).map((part, i) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={i}>{part.slice(2, -2)}</strong>;
                      }
                      if (part === '\n') {
                        return <br key={i} />;
                      }
                      return part;
                    })}
                  </span>
                )}
              </span>
                  
                  {/* Blinking cursor only during streaming */}
                  {isStreaming && (
                    <span className="inline-block w-2 h-4 bg-gray-400 animate-pulse" 
                      style={{ 
                        position: 'absolute',
                        marginLeft: '2px',
                        verticalAlign: 'middle',
                        bottom: '0.1em'
                      }} />
                  )}
                </div>
              </div>
              {showTimestampAndCopy && (
                <div className="flex justify-between items-center mt-2">
                  {isBot && displayContent && (
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-2 py-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                      style={{ paddingLeft: 0 }} // Remove left padding
                    >
                      {copied ? (
                        <>
                          <Check size={'var(--chat-font-size, 14px)'} className="text-green-500" />
                          <span className="text-green-500" style={{ fontSize: 'var(--chat-font-size, 14px)' }}>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy style={{ width: 'var(--chat-font-size, 14px)', height: 'var(--chat-font-size, 14px)' }} />
                          <span className="text-xs text-gray-500" style={{ fontSize: 'var(--chat-font-size, 14px)' }}>Copy</span>
                        </>
                      )}
                    </button>
                  )}
                 <span className="text-xs text-gray-500" style={{ fontSize: 'var(--chat-font-size, 14px)' }}>
                  {message.timestamp.toLocaleTimeString([], { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                  })}
                </span>
                </div>
              )}
            </div>
          )}
          
          {isBot && !isStreaming && displayContent && isLastInSequence && (
            <>
              {(() => {
                const weatherComps = components.filter(c => c.type === 'weather' && c.position === 'after-message');
                //console.log('[WEATHER-DEBUG] Filtering weather components:', weatherComps);
                return weatherComps.map((comp, idx) => (
                  <div key={idx} style={{ 
                    width: 'var(--weather-widget-width, 100%)', // Will use your 100% value,
                    marginTop: 'var(--spacing-md, 16px)'
                  }}>
                    {MessageComponentFactory.renderComponent(comp, {
                      onLocationSelect,
                      onLocationsUpdate,
                      onActionClick,
                      selectedLocation
                    })}
                  </div>
                ));
              })()}
            </>
          )}
          {/* Helper prompts from factory */}
          {isBot && !isStreaming && displayContent && isLastInSequence && (
              <div style={{ 
                marginLeft: 15,
                marginTop: 'var(--spacing-xl)'
              }}>
                {components
                  .filter(c => c.type === 'help' && c.position === 'after-message')
                  .map((comp, idx) => (
                    <div key={idx}>
                      {MessageComponentFactory.renderComponent(comp, {
                        onLocationSelect,
                        onLocationsUpdate,
                        onActionClick,
                        selectedLocation
                      })}
                    </div>
                  ))}
              </div>
            )}

          {/* Flight widget from factory */}
          {isBot && !isStreaming && displayContent && isLastInSequence && (
            <>
              {components
                .filter(c => c.type === 'flight' && c.position === 'after-message')
                .map((comp, idx) => (
                  <div key={idx} className="mt-4">
                    {MessageComponentFactory.renderComponent(comp, {
                      onLocationSelect,
                      onLocationsUpdate,
                      onActionClick,
                      selectedLocation
                    })}
                  </div>
                ))}
            </>
          )}  
        </div>
      </div>

      {isBot && !isStreaming && displayContent && isLastInSequence && (
        <div style={{ 
          marginLeft: 'calc(var(--chat-avatar-size) + var(--spacing-lg))',
          marginTop: 'var(--spacing-xl)'
        }}>
        </div>
      )}

      {/* Extraction Loader */}
      {isBot && message.extractionStatus && 
      message.extractionStatus !== ExtractionStatus.IDLE && 
      message.extractionStatus !== ExtractionStatus.COMPLETE && (
        <div style={{ 
          marginTop: 'var(--spacing-md)',
          width: '100%',
          maxWidth: 'var(--chat-bubble-max-width, 100%)'
        }}>
          <ExtractionLoader 
            status={message.extractionStatus}
            progress={message.extractionProgress}
            startTime={message.extractionStartTime}
          />
        </div>
      )}
      
      {isBot && locations.length > 0 && showLocations && (
      <div style={{ overflow: 'hidden'}}>
        <StackedLocationCarousel
          locations={locations}
          onLocationSelect={onLocationSelect}
          onLocationsUpdate={onLocationsUpdate}
          selectedLocation={selectedLocation}
          LocationRecommendation={LocationRecommendation}
          visibleCards={6}
          cardHeight={10}
          stackOffset={5}
        />
      </div>
    )}
    </div>
  );
}